import { MouseEvent, useState } from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { MonthlyExpenseGroup } from "../../types/monthly-expense.type";
import { formatNumber } from "../../utils";
import { formatTimestamp } from "../DailyExpense/format";
import { CELL_SX } from "./closeTableStyles";
import { COLUMN_GROUP_TITLE, EMPTY_TEXT } from "./constants";
import { ExtraDraft } from "./monthlyExpenseDraft";

const POPOVER_WIDTH = 400;
const ICON_SIZE = 16;

interface ExtrasCellProps {
  branchCode: string;
  branchName: string;
  group: MonthlyExpenseGroup;
  extras: ExtraDraft[];
  /** The group's extras as the current view shows them; null = nothing to divide by. */
  viewAmount: number | null;
  isEditable: boolean;
  monthTitle: string;
  onAdd: (branchCode: string, group: MonthlyExpenseGroup) => void;
  onRemove: (branchCode: string, key: string) => void;
}

interface ExtraLineProps {
  branchCode: string;
  extra: ExtraDraft;
  isEditable: boolean;
  onRemove: (branchCode: string, key: string) => void;
}

function ExtraLine({ branchCode, extra, isEditable, onRemove }: Readonly<ExtraLineProps>) {
  const handleRemove = () => onRemove(branchCode, extra.key);
  const addedText = extra.uuid
    ? `เพิ่มโดย ${extra.createdByName ?? EMPTY_TEXT} · ${formatTimestamp(extra.createdAt)}`
    : "ยังไม่บันทึกร่าง";

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ py: 1, borderBottom: 1, borderColor: "divider" }}>
      <Stack flex={1} minWidth={0}>
        <Typography variant="body2">{extra.description}</Typography>
        <Typography variant="caption" color="text.secondary">
          {addedText}
        </Typography>
      </Stack>
      <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatNumber(extra.amount)}
      </Typography>
      {isEditable && (
        <IconButton size="small" color="error" aria-label={`ลบ ${extra.description}`} onClick={handleRemove}>
          <CloseIcon sx={{ fontSize: ICON_SIZE }} />
        </IconButton>
      )}
    </Stack>
  );
}

/**
 * The เพิ่มเติม column of a group: the extras' sum with their count, opening the list (who added
 * each, remove, add another); an empty one offers + on an editable month.
 */
export default function ExtrasCell({
  branchCode,
  branchName,
  group,
  extras,
  viewAmount,
  isEditable,
  monthTitle,
  onAdd,
  onRemove,
}: Readonly<ExtrasCellProps>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const openList = (event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget);
  const closeList = () => setAnchor(null);
  const handleAdd = () => {
    setAnchor(null);
    onAdd(branchCode, group);
  };
  const groupTitle = COLUMN_GROUP_TITLE[group];

  if (extras.length === 0) {
    return (
      <TableCell sx={CELL_SX}>
        {isEditable ? (
          <IconButton size="small" aria-label={`เพิ่มค่าใช้จ่าย ${branchCode} ${groupTitle}`} onClick={handleAdd}>
            <AddIcon sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        ) : (
          <Typography component="span" variant="body2" color="text.disabled">
            {EMPTY_TEXT}
          </Typography>
        )}
      </TableCell>
    );
  }

  return (
    <TableCell sx={CELL_SX}>
      <Button
        size="small"
        onClick={openList}
        aria-label={`ค่าใช้จ่ายเพิ่มเติม ${branchCode} ${groupTitle}`}
        endIcon={<Chip size="small" label={extras.length} sx={{ height: 18, fontStyle: "italic" }} />}
        sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
      >
        {viewAmount === null ? EMPTY_TEXT : formatNumber(viewAmount)}
      </Button>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={closeList}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: POPOVER_WIDTH } } }}
      >
        <Typography fontWeight={600}>
          ค่าใช้จ่ายเพิ่มเติม · {branchCode} {branchName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          กลุ่ม {groupTitle} · เดือน{monthTitle}
        </Typography>
        {extras.map((extra) => (
          <ExtraLine key={extra.key} branchCode={branchCode} extra={extra} isEditable={isEditable} onRemove={onRemove} />
        ))}
        {isEditable && (
          <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 1 }}>
            เพิ่มรายการ
          </Button>
        )}
      </Popover>
    </TableCell>
  );
}
